import React, { useRef } from 'react';
import { isEqual } from 'lodash-es';
import { isNil } from 'rambdax';
import { numericFormatter } from 'react-number-format';
import { clsx } from 'clsx';
import {
  FocusableProps,
  HelpTextProps,
  InputBase,
  LabelableProps,
  RangeInputBase,
  TextInputBase,
  Validation,
  ValueBase,
} from '@react-types/shared';
import { removeLeadingZero, removeNonNumeric } from '@ping/utils';

import { FieldGroup } from '../FieldGroup';
import style from './style.module.scss';

export interface INumberFieldProps
  extends InputBase,
    Validation<any>,
    FocusableProps,
    TextInputBase,
    ValueBase<string>,
    RangeInputBase<string>,
    LabelableProps,
    HelpTextProps,
    ICustomizable {
  /**
   * Formatting options for the value displayed in the number field.
   * This also affects what characters are allowed to be typed by the user.
   */
  formatOptions?: Intl.NumberFormatOptions;
  name?: string;
  /** A config to handle border-radius and spacing when it used inside ComboField component */
  magnet?: IMagnet;
}

const NUMERICS = new Set('9876543210'.split(''));
const OPERATIONS = new Set(['Backspace', 'Delete', 'Tab', 'ArrowRight', 'ArrowLeft']);
const DECIMAL_POINT = '.' as const;
const DECIMAL_COMMA = ',' as const;
const DECIMAL_STARTS_WITH_ZERO = '0.' as const;
const ZERO = '0' as const;

const _NumberField = (props: INumberFieldProps) => {
  //
  // Inject number field aria attributes
  const inputRef = useRef<HTMLInputElement>();
  // const { labelProps, groupProps, inputProps } = useNumberField(props, state, inputRef);

  const normalizeOnKeyDown = (ev: React.KeyboardEvent<HTMLInputElement>) => {
    const text = inputRef.current.value;
    //
    // allow only valid numeric characters
    const isNumeric = NUMERICS.has(ev.key);
    const isAllowedOperation = OPERATIONS.has(ev.key);
    const isDecimal = text.includes(DECIMAL_POINT);
    const isDecimalPointAllowed = !isDecimal && (DECIMAL_POINT === ev.key || DECIMAL_COMMA === ev.key); // allow only one decimal point
    const isMetaOperation = ev.metaKey || ev.ctrlKey;

    if (!isNumeric && !isDecimalPointAllowed && !isAllowedOperation && !isMetaOperation) {
      ev.preventDefault();
    }
  };

  const handleOnChange = () => {
    const text = removeNonNumeric(inputRef.current.value);
    //
    // prevent more than one leading zero
    const isDuplicateZero = text.startsWith('00');
    if (isDuplicateZero) {
      return;
    }

    //
    // ensures that the input value is always a valid number and prevents errors
    // when performing calculations or using the input value in other parts of the code.
    const isDecimal =
      (text.startsWith(DECIMAL_POINT) && text.endsWith(DECIMAL_POINT)) ||
      (text.startsWith(DECIMAL_COMMA) && text.endsWith(DECIMAL_COMMA));
    if (isDecimal) {
      props.onChange?.('0.');
      return;
    }

    //
    // prevent more than allowed decimals count
    const [, decimal] = text.includes(DECIMAL_COMMA) ? text.split(DECIMAL_COMMA) : text.split(DECIMAL_POINT);
    const isDecimalMaximum = decimal?.length > props.formatOptions?.maximumFractionDigits;
    if (isDecimalMaximum) {
      return;
    }

    //
    // prevent adding integer numbers after zero
    // example:
    // 03   returns  ==>  0
    // 0.3  returns  ==>  0.3
    const isIntegerNumberStartsWithZero = text.startsWith(ZERO) && !text.startsWith(DECIMAL_STARTS_WITH_ZERO);
    if (isIntegerNumberStartsWithZero) {
      props.onChange?.(ZERO);
      return;
    }

    props.onChange?.(removeLeadingZero(text.replace(DECIMAL_COMMA, DECIMAL_POINT)));
  };

  const normalizeOnBlur = () => {
    const text = inputRef.current.value;

    //
    // remove tail decimal point
    if (text.endsWith(DECIMAL_POINT) || text.endsWith(DECIMAL_COMMA)) {
      props.onChange?.(text.slice(0, -1));
    }
  };

  return (
    <FieldGroup className={clsx('grow', props.className)}>
      {!isNil(props.label) && <FieldGroup.Label>{props.label}</FieldGroup.Label>}

      <FieldGroup.Control magnet={props.magnet}>
        <input
          className={style['input-number']}
          name={props.name}
          ref={inputRef}
          type='text'
          inputMode='decimal'
          autoComplete='off'
          placeholder={props.placeholder}
          value={numericFormatter(removeNonNumeric(props.value), { thousandSeparator: true })}
          disabled={props.isDisabled}
          readOnly={props.isReadOnly}
          onKeyDown={normalizeOnKeyDown}
          onBlur={normalizeOnBlur}
          onChange={handleOnChange}
        />
      </FieldGroup.Control>
    </FieldGroup>
  );
};

export const NumberField = React.memo(
  _NumberField,
  (prev, next) =>
    prev.isDisabled === next.isDisabled &&
    prev.isReadOnly === next.isReadOnly &&
    prev.value === next.value &&
    prev.minValue === next.minValue &&
    prev.maxValue === next.maxValue &&
    prev.defaultValue === next.defaultValue &&
    isEqual(prev.formatOptions, next.formatOptions)
);
